/**
 * TrendChart — Line/Area time-series wrapper over Recharts (P0 kit). Colors are
 * `var(--g-*)` strings (dark-mode-correct, no JS). Wraps the chart in
 * AdminStates for the loading/empty/error contract and exposes `role="img"` +
 * aria-label so a screen reader gets the chart's meaning (the SVG itself is
 * aria-hidden). Design: _shared/KIT-SPEC.md.
 */
import { type ReactElement } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AdminStates } from "@/features/admin/_shared/AdminStates";
import { CHART, toneColor, type ChartTone } from "@/features/admin/_shared/chartTokens";

export interface TrendPoint {
  x: string;
  y: number;
}
export interface TrendChartProps {
  data: readonly TrendPoint[];
  mode?: "line" | "area";
  tone?: ChartTone;
  height?: number;
  isLoading?: boolean;
  isError?: boolean;
  loadingLabel: string;
  emptyLabel: string;
  errorLabel: string;
  yTickFormatter?: (value: number) => string;
  ariaLabel: string;
  testid?: string;
}

const axisTick = { fill: CHART.axis, fontSize: 11 };

export function TrendChart({
  data,
  mode = "line",
  tone = "primary",
  height = 240,
  isLoading = false,
  isError = false,
  loadingLabel,
  emptyLabel,
  errorLabel,
  yTickFormatter,
  ariaLabel,
  testid = "trend-chart",
}: TrendChartProps): ReactElement {
  const color = toneColor(tone);
  const tooltipStyle = {
    background: CHART.surface,
    border: `1px solid ${CHART.grid}`,
    borderRadius: 10,
    color: CHART.ink,
    fontSize: 12,
  } as const;

  return (
    <AdminStates
      isLoading={isLoading}
      isError={isError}
      isEmpty={data.length === 0}
      loadingLabel={loadingLabel}
      emptyLabel={emptyLabel}
      errorLabel={errorLabel}
      testid={testid}
    >
      <div data-testid={testid} role="img" aria-label={ariaLabel} style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          {mode === "area" ? (
            <AreaChart data={data as TrendPoint[]} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={`${testid}-fill`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={CHART.grid} vertical={false} />
              <XAxis dataKey="x" tick={axisTick} stroke={CHART.grid} tickLine={false} />
              <YAxis tick={axisTick} stroke={CHART.grid} tickLine={false} width={36} tickFormatter={yTickFormatter} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: CHART.grid }} />
              <Area type="monotone" dataKey="y" stroke={color} strokeWidth={2} fill={`url(#${testid}-fill)`} isAnimationActive={false} />
            </AreaChart>
          ) : (
            <LineChart data={data as TrendPoint[]} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={CHART.grid} vertical={false} />
              <XAxis dataKey="x" tick={axisTick} stroke={CHART.grid} tickLine={false} />
              <YAxis tick={axisTick} stroke={CHART.grid} tickLine={false} width={36} tickFormatter={yTickFormatter} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: CHART.grid }} />
              <Line type="monotone" dataKey="y" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </AdminStates>
  );
}

export default TrendChart;
